import { Check, X, Sparkles, Building2 } from 'lucide-react';
import { Card, CardContent, Button, Badge } from '@/components/ui';
import type { SubscriptionPlan, BillingInterval, FeatureName } from '@/types/subscription';

// Feature categories for display
const FEATURE_CATEGORIES = {
  core: {
    label: 'Core Features',
    features: ['basicReporting', 'workOrders', 'vendorPortal', 'assetTracking'] as FeatureName[],
  },
  analytics: {
    label: 'Analytics',
    features: ['basicAnalytics', 'advancedAnalytics', 'customReports'] as FeatureName[],
  },
  iot: {
    label: 'IoT & Smart Devices',
    features: ['iotMonitoring', 'smartLocks', 'leakSensors', 'thermostats', 'smokeDetectors', 'motionSensors', 'autoAlertReports'] as FeatureName[],
  },
  integrations: {
    label: 'Integrations',
    features: ['emailIntegration', 'smsNotifications', 'apiAccess', 'webhooks', 'customIntegrations'] as FeatureName[],
  },
  enterprise: {
    label: 'Enterprise',
    features: ['sso', 'auditLog', 'whiteLabel', 'dedicatedSupport', 'slaGuarantee'] as FeatureName[],
  },
};

const FEATURE_LABELS: Record<FeatureName, string> = {
  basicReporting: 'Issue Reporting',
  workOrders: 'Work Orders',
  vendorPortal: 'Vendor Portal',
  assetTracking: 'Asset Tracking',
  basicAnalytics: 'Basic Analytics',
  advancedAnalytics: 'Advanced Analytics',
  customReports: 'Custom Reports',
  iotMonitoring: 'IoT Dashboard',
  smartLocks: 'Smart Locks',
  leakSensors: 'Leak Sensors',
  thermostats: 'Smart Thermostats',
  smokeDetectors: 'Smoke Detectors',
  motionSensors: 'Motion Sensors',
  autoAlertReports: 'Auto Alert Reports',
  // Disruptor Features
  qrCodeWorkflows: 'QR Code Workflows',
  magicLinkVendorPortal: 'Magic Link Vendor Portal',
  floorPlanMapping: 'Floor Plan Mapping',
  // Integrations
  emailIntegration: 'Email Notifications',
  smsNotifications: 'SMS Notifications',
  apiAccess: 'API Access',
  webhooks: 'Webhooks',
  customIntegrations: 'Custom Integrations',
  sso: 'Single Sign-On',
  auditLog: 'Audit Log',
  whiteLabel: 'White Label',
  dedicatedSupport: 'Dedicated Support',
  slaGuarantee: 'SLA Guarantee',
};

interface PricingCardProps {
  plan: SubscriptionPlan;
  interval: BillingInterval;
  isCurrentPlan?: boolean;
  onSelect?: (plan: SubscriptionPlan) => void;
  showFeatures?: boolean;
  compact?: boolean;
}

export function PricingCard({
  plan,
  interval,
  isCurrentPlan = false,
  onSelect,
  showFeatures = true,
  compact = false,
}: PricingCardProps) {
  const price = interval === 'annual' ? plan.pricing.annual : plan.pricing.monthly;
  const perUnitPrice = interval === 'annual' ? plan.pricing.perUnitAnnual : plan.pricing.perUnitMonthly;
  const annualSavings = plan.pricing.monthly > 0
    ? Math.round(((plan.pricing.monthly - plan.pricing.annual) / plan.pricing.monthly) * 100)
    : 0;

  // Key features to highlight (most important ones)
  const keyFeatures: FeatureName[] = [
    'workOrders',
    'vendorPortal',
    'iotMonitoring',
    'smartLocks',
    'advancedAnalytics',
    'apiAccess',
  ];

  return (
    <Card
      className={`relative overflow-hidden transition-all duration-200 ${
        plan.popular
          ? 'ring-2 ring-primary-500 dark:ring-primary-400 shadow-lg dark:shadow-primary-500/20'
          : isCurrentPlan
          ? 'ring-2 ring-success-500 dark:ring-success-400'
          : 'hover:shadow-md dark:hover:shadow-primary-500/10'
      }`}
    >
      {plan.popular && (
        <div className="absolute top-0 right-0">
          <div className="flex items-center gap-1 bg-primary-500 text-white text-xs font-medium px-3 py-1 rounded-bl-lg">
            <Sparkles className="h-3 w-3" />
            Most Popular
          </div>
        </div>
      )}
      {isCurrentPlan && !plan.popular && (
        <div className="absolute top-0 right-0">
          <Badge color="green" className="rounded-bl-lg rounded-tr-none rounded-br-none rounded-tl-none">
            Current Plan
          </Badge>
        </div>
      )}

      <CardContent className={compact ? 'p-4' : 'p-6'}>
        {/* Plan name and description */}
        <div className="mb-4">
          <h3 className="text-xl font-bold text-secondary-900 dark:text-secondary-100">
            {plan.name}
          </h3>
          <p className="mt-1 text-sm text-secondary-500 dark:text-secondary-400">
            {plan.description}
          </p>
        </div>

        {/* Pricing */}
        <div className="mb-6">
          <div className="flex items-baseline gap-1">
            <span className="text-4xl font-bold text-secondary-900 dark:text-secondary-100">
              ${price}
            </span>
            <span className="text-secondary-500 dark:text-secondary-400">/mo</span>
          </div>
          {perUnitPrice > 0 && (
            <p className="mt-1 text-sm text-secondary-500 dark:text-secondary-400">
              + ${perUnitPrice}/unit/mo after {plan.limits.maxUnits} units
            </p>
          )}
          {interval === 'annual' && annualSavings > 0 && (
            <p className="mt-1 text-sm text-success-600 dark:text-success-400 font-medium">
              Save {annualSavings}% with annual billing
            </p>
          )}
        </div>

        {/* CTA Button */}
        <Button
          variant={plan.popular ? 'primary' : isCurrentPlan ? 'outline' : 'secondary'}
          className="w-full mb-6"
          onClick={() => onSelect?.(plan)}
          disabled={isCurrentPlan}
        >
          {isCurrentPlan ? 'Current Plan' : plan.tier === 'enterprise' ? 'Contact Sales' : 'Choose Plan'}
        </Button>

        {/* Limits */}
        <div className="mb-6 pb-6 border-b border-secondary-100 dark:border-white/[0.06]">
          <div className="grid grid-cols-2 gap-3">
            <LimitItem
              label="Units"
              value={plan.limits.maxUnits ?? 'Unlimited'}
            />
            <LimitItem
              label="Users"
              value={plan.limits.maxUsers ?? 'Unlimited'}
            />
            <LimitItem
              label="Properties"
              value={plan.limits.maxProperties ?? 'Unlimited'}
            />
            <LimitItem
              label="IoT Devices"
              value={plan.limits.maxDevices ?? 'Unlimited'}
            />
          </div>
        </div>

        {/* Features */}
        {showFeatures && (
          <div className="space-y-2">
            <p className="text-xs font-semibold uppercase tracking-wider text-secondary-500 dark:text-secondary-400 mb-3">
              Key Features
            </p>
            {keyFeatures.map((feature) => (
              <FeatureItem
                key={feature}
                label={FEATURE_LABELS[feature]}
                included={plan.features[feature]}
              />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function LimitItem({ label, value }: { label: string; value: number | string }) {
  return (
    <div className="flex items-center gap-2">
      <Building2 className="h-4 w-4 text-secondary-400 dark:text-secondary-500" />
      <div className="text-sm">
        <span className="font-semibold text-secondary-900 dark:text-secondary-100">
          {value}
        </span>{' '}
        <span className="text-secondary-500 dark:text-secondary-400">{label}</span>
      </div>
    </div>
  );
}

function FeatureItem({ label, included }: { label: string; included: boolean }) {
  return (
    <div className="flex items-center gap-2">
      {included ? (
        <Check className="h-4 w-4 text-success-500" />
      ) : (
        <X className="h-4 w-4 text-secondary-300 dark:text-secondary-600" />
      )}
      <span
        className={`text-sm ${
          included
            ? 'text-secondary-700 dark:text-secondary-300'
            : 'text-secondary-400 dark:text-secondary-600'
        }`}
      >
        {label}
      </span>
    </div>
  );
}

// Full feature comparison table
interface FeatureComparisonProps {
  plans: SubscriptionPlan[];
  interval: BillingInterval;
}

export function FeatureComparison({ plans, interval }: FeatureComparisonProps) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="border-b border-secondary-200 dark:border-white/[0.06]">
            <th className="pb-4 text-left text-sm font-semibold text-secondary-900 dark:text-secondary-100">
              Features
            </th>
            {plans.map((plan) => (
              <th
                key={plan.tier}
                className="pb-4 text-center text-sm font-semibold text-secondary-900 dark:text-secondary-100"
              >
                {plan.name}
                <div className="mt-1 text-lg font-bold text-primary-600 dark:text-primary-400">
                  ${interval === 'annual' ? plan.pricing.annual : plan.pricing.monthly}/mo
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Object.entries(FEATURE_CATEGORIES).map(([key, category]) => (
            <>
              <tr key={`${key}-header`} className="border-b border-secondary-100 dark:border-white/[0.03]">
                <td
                  colSpan={plans.length + 1}
                  className="pt-6 pb-2 text-xs font-semibold uppercase tracking-wider text-secondary-500 dark:text-secondary-400"
                >
                  {category.label}
                </td>
              </tr>
              {category.features.map((feature) => (
                <tr
                  key={feature}
                  className="border-b border-secondary-50 dark:border-white/[0.02]"
                >
                  <td className="py-3 text-sm text-secondary-700 dark:text-secondary-300">
                    {FEATURE_LABELS[feature]}
                  </td>
                  {plans.map((plan) => (
                    <td key={`${plan.tier}-${feature}`} className="py-3 text-center">
                      {plan.features[feature] ? (
                        <Check className="mx-auto h-5 w-5 text-success-500" />
                      ) : (
                        <X className="mx-auto h-5 w-5 text-secondary-300 dark:text-secondary-600" />
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </>
          ))}
        </tbody>
      </table>
    </div>
  );
}
